import { Tabs } from '@design-systems/canvas-ui'
import { PageTabs } from 'components/page-tabs'
import { useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { isStore } from 'utils/location'
import { useLogClick } from 'utils/analytics'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { MPMLayout } from 'components/layout'
import { ErrorFallback } from 'components/error-fallback'

const ROUTES = {
    LAGGING: 'lagging',
    TOTAL: 'total',
    LEADING: 'leading',
}

export const SafetyPage = () => {
    const { currentLocation, t } = useEssentials()
    const { safety } = TRANSLATION_KEYS
    const { tabs: tabKeys, title } = safety
    const { logClick } = useLogClick('safety')
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState<string>(ROUTES.LAGGING)
    const { pathname, search } = useLocation()
    const isStoreFlag = isStore(currentLocation)

    const tabsWithTranslations = useMemo(() => {
        if (isStore(currentLocation)) {
            return []
        }
        const tabs = [
            { key: ROUTES.LAGGING, label: t(tabKeys.lagging) },
            { key: ROUTES.TOTAL, label: t(tabKeys.total) },
            { key: ROUTES.LEADING, label: t(tabKeys.leading) },
        ]
        return tabs
    }, [currentLocation, t, tabKeys])

    const storePath = `/safety/store${search}`

    const handleTabSelect = (activeTab: string) => {
        setActiveTab(activeTab)
        logClick('safety tab ' + activeTab)
        if (!isStoreFlag) {
            void navigate(`/safety/${activeTab}${search}`)
        } else {
            void navigate(storePath, { replace: true })
        }
    }

    useEffect(() => {
        if (isStoreFlag && pathname !== storePath) {
            void navigate(storePath, { replace: true })
        } else if (!isStoreFlag) {
            const tabPath = `/safety/${activeTab}${search}`
            if (pathname !== tabPath) {
                void navigate(tabPath, { replace: true })
            }
        }
    }, [isStoreFlag, activeTab, pathname, search, navigate, storePath])

    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    heading={t(title)}
                />
            )}
        >
            <MPMLayout
                heading={t(title)}
                className="hc8-ph-none safety-page"
                hasTabs={!isStoreFlag}
                tabs={
                    !isStoreFlag && (
                        <PageTabs
                            activeTab={activeTab}
                            className="tabs hc8-pv-2x"
                            disableAdaptive={true}
                            onTabSelect={handleTabSelect}
                        >
                            {tabsWithTranslations.map((tab) => (
                                <Tabs.Item key={tab.key} name={tab.key}>
                                    {tab.label}
                                </Tabs.Item>
                            ))}
                        </PageTabs>
                    )
                }
            >
                {isStoreFlag ? (
                    <div className="hc8-ph-4x">
                        <Outlet />
                    </div>
                ) : (
                    <Outlet />
                )}
            </MPMLayout>
        </ErrorBoundary>
    )
}
